import { supabase } from '@/lib/supabase/client'

export interface KpiData {
  totalUsers: number
  activeSubscribers: number
  tierCounts: { trial: number; pilot: number; pro: number; business: number; expired: number }
  mrr: number
  /** Active subscribers and monthly-equivalent revenue keyed `${tier}_${cycle}`.
   *  Optional: an admin-stats deployment older than this field omits it. */
  mrrByPlan?: Record<string, { count: number; mrr: number }>
  activeLayouts: number
  newThisWeek: number
}

export interface SignupData {
  signups: { date: string; count: number }[]
}

export interface ActivityEvent {
  type: 'signup' | 'admin_action'
  timestamp: string
  // signup fields
  user_id?: string
  email?: string
  display_name?: string | null
  // admin action fields
  admin_id?: string
  action?: string
  target_type?: string
  target_id?: string
  details?: Record<string, unknown>
}

export interface ActivityData {
  events: ActivityEvent[]
}

async function invokeAdminStats<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-stats', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

export function fetchKpis() {
  return invokeAdminStats<KpiData>({ action: 'kpis' })
}

export function fetchSignups(days: number) {
  return invokeAdminStats<SignupData>({ action: 'signups', days })
}

export function fetchActivity(limit: number) {
  return invokeAdminStats<ActivityData>({ action: 'activity', limit })
}

export interface CatalogUsageRow {
  catalog_id: string
  display_name: string
  category: string
  placements: number
  unique_layouts: number
}

export interface CatalogUsageData {
  total_layouts_scanned: number
  rows: CatalogUsageRow[]
}

export function fetchCatalogUsage() {
  return invokeAdminStats<CatalogUsageData>({ action: 'catalog_usage' })
}
